import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Paperclip, Loader2, FileText, Plus, Trash2, Check, ArrowRight, AlertTriangle } from "lucide-react";

const STATUSES = ["New Intake","Consult Scheduled","Active Strategy","Gathering Evidence","Ready to File","Filed and Waiting","Decision Received"];
const NOTE_KINDS = [
  { v: "note", l: "Note" },
  { v: "statement", l: "Lay statement" },
  { v: "nexus", l: "Nexus letter" },
  { v: "witness", l: "Witness / buddy" },
];
const FILE_KINDS = ["Lay Statements","Witness (Buddy) Statements","Medical Records","Nexus Letters","Rating Decisions and VA Letters","Other"];

export default function VeteranDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [claims, setClaims] = useState([]);
  const [claimsMeta, setClaimsMeta] = useState(null);
  const [newClaim, setNewClaim] = useState({ claim_type: "New Claim", current_status: "Intake Form Submitted" });
  const [creatingClaim, setCreatingClaim] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteKind, setNoteKind] = useState("note");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileKind, setFileKind] = useState("Medical Records");
  const [newTask, setNewTask] = useState({ title: "", detail: "", due_date: "" });
  const [notFound, setNotFound] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    try {
      const [d, t, c] = await Promise.all([
        api.get(`/veterans/${id}`),
        api.get(`/veterans/${id}/tasks`),
        api.get(`/veterans/${id}/claims`),
      ]);
      setData(d.data);
      setTasks(t.data);
      setClaims(c.data);
      if (!claimsMeta) {
        const m = await api.get("/admin/claims/meta");
        setClaimsMeta(m.data);
      }
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error(err.response?.data?.detail || "Could not load veteran");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await api.post(`/veterans/${id}/notes`, { text: noteText, kind: noteKind });
      setNoteText("");
      toast.success("Note added");
      load();
    } finally { setSaving(false); }
  };

  const addTask = async () => {
    if (!newTask.title.trim()) return toast.error("Task title is required");
    await api.post(`/veterans/${id}/tasks`, newTask);
    setNewTask({ title: "", detail: "", due_date: "" });
    toast.success("Task assigned");
    load();
  };
  const toggleTask = async (t) => { await api.patch(`/veterans/${id}/tasks/${t.task_id}`, { done: !t.done }); load(); };
  const removeTask = async (tid) => { await api.delete(`/veterans/${id}/tasks/${tid}`); load(); };

  const addClaim = async () => {
    setCreatingClaim(true);
    try {
      await api.post(`/veterans/${id}/claims`, newClaim);
      setNewClaim({ claim_type: "New Claim", current_status: "Intake Form Submitted" });
      toast.success("Claim created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create claim");
    } finally {
      setCreatingClaim(false);
    }
  };
  const removeClaim = async (cid) => {
    if (!window.confirm("Delete this claim? The veteran will no longer see it.")) return;
    await api.delete(`/claims/${cid}`);
    toast.success("Claim deleted");
    load();
  };

  const upload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", fileKind);
      await api.post(`/veterans/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${f.name} uploaded`);
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileInput.current) fileInput.current.value = ""; }
  };

  const updateStatus = async (status) => { await api.patch(`/veterans/${id}/status`, { status }); toast.success(`Status: ${status}`); load(); };

  if (!user || user.role !== "admin") return <div className="p-24 text-center">Admin only.</div>;
  if (notFound) return (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center" data-testid="vet-not-found">
      <h1 className="font-display text-3xl">Veteran not found.</h1>
      <p className="text-primary/70 mt-3">This record may have been deleted. Head back to the pipeline.</p>
      <Link to="/admin" className="mt-6 inline-flex h-11 items-center px-5 bg-accent text-accent-foreground font-semibold">← Back to admin</Link>
    </div>
  );
  if (!data) return <div className="grid place-items-center h-96"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const v = data.veteran;
  const filesByCat = {};
  FILE_KINDS.forEach((c) => (filesByCat[c] = []));
  (v.files || []).forEach((f) => {
    const c = FILE_KINDS.includes(f.kind) ? f.kind : "Other";
    filesByCat[c].push(f);
  });

  return (
    <div className="mx-auto max-w-[1600px] px-5 lg:px-10 py-8">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-primary/60 hover:text-primary font-semibold" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <div className="mt-6 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          {/* Header */}
          <div>
            <div className="label-caps text-primary/70">{(v.branches || []).join(", ") || v.branch || "Branch n/a"} · {v.job_title || v.mos || "Job n/a"}</div>
            <h1 className="font-display text-4xl lg:text-5xl mt-2">{v.full_name || `${v.first_name || ""} ${v.last_name || ""}`}</h1>
            <div className="mt-4 flex flex-wrap gap-2">
              {v.current_rating > 0 && <Badge className="rounded-none bg-primary text-primary-foreground hover:bg-primary">Current: {v.current_rating}%</Badge>}
              {v.state && <Badge variant="secondary" className="rounded-none">{v.state}</Badge>}
              {v.primary_goal && <Badge variant="outline" className="rounded-none">Goal: {v.primary_goal}</Badge>}
              {v.timeframe && <Badge variant="outline" className="rounded-none">{v.timeframe}</Badge>}
            </div>
          </div>

          {/* Email delivery warning */}
          {v.email_status === "failed" && (
            <Card className="p-4 border-2 border-destructive bg-destructive/10" data-testid="email-failed-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div className="flex-1">
                  <div className="font-semibold text-destructive">Emails to this veteran are bouncing.</div>
                  <div className="text-sm text-primary/80 mt-1">
                    SendGrid reported: <span className="font-mono">{v.email_failed_reason || "invalid recipient"}</span>. Contact them by phone or update their email address, this flag will auto-clear the next time an email to them successfully delivers.
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Intake summary */}
          <Card className="p-6 border-2" data-testid="intake-summary">
            <div className="label-caps mb-4">Intake summary</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {[
                ["Email", v.email], ["Phone", v.phone], ["State", v.state], ["Age range", v.age_range],
                ["Service years", v.service_start_year && `${v.service_start_year} – ${v.service_end_year}`],
                ["Discharge", v.discharge_type],
                ["Aboard vessel", v.ship_service ? "Yes" : v.ship_service === false ? "No" : null],
                ["Vessels", v.vessels],
                ["Currently rated", v.has_rating === true ? "Yes" : v.has_rating === false ? "No" : null],
                ["SC conditions", v.conditions_sc],
                ["Denied claim", v.has_denied_claim ? `${v.denied_when || "?"} · ${v.denied_for || "?"}` : null],
                ["Seen at VA", v.seen_at_va === true ? "Yes" : v.seen_at_va === false ? "No" : null],
                ["Portal account", v.vagov_service],
                ["Service med. records", v.service_med_records],
                ["Private/VA records", v.private_med_records],
                ["Working with (VSO/agent)", v.currently_working_with_who],
                ["Willing to gather", v.willing_gather === true ? "Yes" : v.willing_gather === false ? "No" : null],
                ["Heard about VDE via", v.hears_about_us],
              ].filter(([, val]) => val != null && val !== "").map(([k, val]) => (
                <div key={k} className="border-b border-border pb-2">
                  <div className="text-xs text-primary/60 uppercase tracking-widest">{k}</div>
                  <div className="text-primary">{String(val)}</div>
                </div>
              ))}
            </div>
            {v.additional_info && (
              <div className="mt-5 border-l-4 border-accent pl-4">
                <div className="label-caps text-primary/70 mb-1">Additional notes</div>
                <p className="text-sm whitespace-pre-wrap">{v.additional_info}</p>
              </div>
            )}
          </Card>

          {/* Tasks */}
          <Card className="p-6 border-2" data-testid="admin-tasks">
            <div className="flex items-center justify-between mb-4">
              <div className="label-caps">Next Steps for veteran</div>
              <span className="text-xs text-primary/60 font-mono">{tasks.filter(t=>t.done).length}/{tasks.length} done</span>
            </div>

            <div className="grid md:grid-cols-6 gap-3 mb-5">
              <Input placeholder="Task title" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} className="md:col-span-2 h-11" data-testid="task-title" />
              <Input placeholder="Detail (optional)" value={newTask.detail} onChange={(e) => setNewTask({ ...newTask, detail: e.target.value })} className="md:col-span-2 h-11" data-testid="task-detail" />
              <Input type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} className="h-11" data-testid="task-due" />
              <Button onClick={addTask} className="h-11" data-testid="task-add"><Plus className="w-4 h-4" /> Add</Button>
            </div>

            {tasks.length === 0 ? (
              <div className="text-center py-6 text-primary/60 border-2 border-dashed border-border">No tasks yet.</div>
            ) : (
              <ul className="space-y-2">
                {tasks.map((t) => (
                  <li key={t.task_id} className={`flex items-start gap-3 p-3 border-2 ${t.done ? "bg-secondary" : ""}`} data-testid={`admin-task-${t.task_id}`}>
                    <button onClick={() => toggleTask(t)} className={`mt-0.5 w-5 h-5 border-2 flex items-center justify-center ${t.done ? "bg-primary border-primary" : "border-primary"}`}>
                      {t.done && <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />}
                    </button>
                    <div className="flex-1 text-sm">
                      <div className={t.done ? "line-through text-primary/50" : "font-semibold"}>{t.title}</div>
                      {t.detail && <div className="text-xs text-primary/70 mt-0.5">{t.detail}</div>}
                      {t.due_date && <div className="text-xs text-primary/50 mt-1 font-mono">Due {t.due_date}</div>}
                    </div>
                    <button onClick={() => removeTask(t.task_id)} className="text-destructive/60 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Claims (VA-style pipeline) */}
          <Card className="p-6 border-2" data-testid="admin-vet-claims">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-caps">Claim status tracker</div>
                <p className="text-xs text-primary/60 mt-1">Create a claim so the veteran sees their live progress in the portal.</p>
              </div>
              <Link to="/admin/claims" className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
                All claims <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {claimsMeta && (
              <div className="grid md:grid-cols-6 gap-3 mb-5" data-testid="new-claim-form">
                <div className="md:col-span-2">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-primary/70">Claim type</label>
                  <Select value={newClaim.claim_type} onValueChange={(v) => {
                    setNewClaim({ claim_type: v, current_status: v === "Higher-Level Review" && claimsMeta.hlr_skip.includes(newClaim.current_status) ? "Intake Form Submitted" : newClaim.current_status });
                  }}>
                    <SelectTrigger className="mt-1 h-11" data-testid="new-claim-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{claimsMeta.claim_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-primary/70">Start at status</label>
                  <Select value={newClaim.current_status} onValueChange={(v) => setNewClaim({ ...newClaim, current_status: v })}>
                    <SelectTrigger className="mt-1 h-11" data-testid="new-claim-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {claimsMeta.stages
                        .filter((s) => !(newClaim.claim_type === "Higher-Level Review" && claimsMeta.hlr_skip.includes(s)))
                        .map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={addClaim} disabled={creatingClaim} className="h-11 mt-auto" data-testid="new-claim-add">
                  <Plus className="w-4 h-4" /> {creatingClaim ? "…" : "Add"}
                </Button>
              </div>
            )}

            {claims.length === 0 ? (
              <div className="text-center py-6 text-primary/60 border-2 border-dashed border-border text-sm">
                No claim on file yet. Create one above to activate the veteran's status tracker.
              </div>
            ) : (
              <ul className="space-y-2">
                {claims.map((c) => (
                  <li key={c.claim_id} className="flex items-center gap-3 p-3 border-2 border-border" data-testid={`claim-row-${c.claim_id}`}>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{c.claim_type}</div>
                      <div className="text-xs text-primary/60 mt-0.5">
                        {c.current_status}
                        {c.waiting_on && <span className="ml-2 font-mono text-[10px] uppercase text-primary">· waiting on {c.waiting_on}</span>}
                      </div>
                    </div>
                    <Link to="/admin/claims" className="text-xs font-semibold text-primary hover:underline">Update</Link>
                    <button onClick={() => removeClaim(c.claim_id)} className="text-destructive/60 hover:text-destructive" data-testid={`claim-delete-${c.claim_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Private internal notes */}
          <Card className="p-6 border-2 bg-secondary/50" data-testid="admin-notes">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-caps">Private internal notes</div>
                <p className="text-xs text-primary/60 mt-1">Only admin sees these. Never visible to the veteran.</p>
              </div>
              <span className="text-xs text-primary/60 font-mono">{data.notes.length} entries</span>
            </div>
            <div className="flex gap-3 mb-3">
              <Select value={noteKind} onValueChange={setNoteKind}>
                <SelectTrigger className="w-44 h-11 bg-white" data-testid="note-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{NOTE_KINDS.map((k) => (<SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <Textarea placeholder="Timestamped internal note (draft statements, call notes, milestones)…" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} className="bg-white" data-testid="note-text" />
            <div className="mt-3 flex justify-end">
              <Button onClick={addNote} disabled={saving} data-testid="note-save">{saving ? "Saving…" : "Add note"}</Button>
            </div>
            <div className="mt-6 space-y-4">
              {data.notes.map((n) => (
                <div key={n.note_id} className="bg-white border-l-4 border-primary/30 p-3">
                  <div className="flex items-center gap-2 text-xs text-primary/60 font-mono">
                    <span className="uppercase text-primary/80">{n.kind}</span> · {new Date(n.created_at).toLocaleString()}
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{n.text}</p>
                </div>
              ))}
              {data.notes.length === 0 && <div className="text-sm text-primary/60 text-center py-4">No notes yet.</div>}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <Card className="p-6 border-2">
            <div className="label-caps mb-3">Status</div>
            <Select value={v.status} onValueChange={updateStatus}>
              <SelectTrigger className="h-11" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select>
            <div className="mt-3 text-xs text-primary/60">Updated {new Date(v.updated_at).toLocaleDateString()}</div>
          </Card>

          <Card className="p-6 border-2" data-testid="admin-documents">
            <div className="label-caps mb-3">Documents</div>
            <div className="flex gap-2 mb-3">
              <select value={fileKind} onChange={(e) => setFileKind(e.target.value)} className="flex-1 h-11 px-3 border-2 border-input bg-white font-semibold text-sm" data-testid="file-kind">
                {FILE_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 border-2 border-dashed border-border p-4 cursor-pointer hover:border-primary" data-testid="file-drop">
              <Paperclip className="w-4 h-4 text-primary/60" />
              <span className="text-sm text-primary/70">{uploading ? "Uploading…" : "Upload document"}</span>
              <input ref={fileInput} type="file" hidden onChange={upload} accept=".pdf,.jpg,.jpeg,.png,.docx" />
            </label>

            <div className="mt-5 space-y-4">
              {FILE_KINDS.map((cat) => (
                filesByCat[cat].length > 0 && (
                  <div key={cat}>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-primary/60 mb-1">{cat}</div>
                    <ul className="space-y-1">
                      {filesByCat[cat].map((f) => (
                        <li key={f.file_id}>
                          <a href={`${API}/files/${encodeURI(f.storage_path || "")}`} target="_blank" rel="noreferrer"
                             className="flex items-center gap-2 text-xs text-primary hover:underline">
                            <FileText className="w-3.5 h-3.5" />
                            <span className="truncate flex-1">{f.filename || f.original_filename}</span>
                            {f.uploaded_by === "veteran" && <span className="text-[9px] font-mono text-accent-foreground bg-accent px-1">VET</span>}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ))}
              {(v.files || []).length === 0 && <div className="text-xs text-primary/60 text-center py-3">No files yet.</div>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
